import React, { useMemo, useState, memo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Bed, Bath, Maximize2, Star, ImageIcon } from 'lucide-react';

/**
 * FeaturedPropertyCard
 * ------------------------------------------------------------------
 * Card premium de imóvel para usar dentro de <HorizontalScroll />.
 * Estilo Airbnb/Zillow/QuintoAndar: imagen con overlay, precio em
 * destaque, badges de "Destaque" e status, specs com ícones.
 *
 * Uso:
 * <HorizontalScroll title="Imóveis em Destaque">
 *   {imoveis.map((p) => (
 *     <FeaturedPropertyCard
 *       key={p.id}
 *       property={p}
 *       onClick={(prop) => navigate(`/imovel/${prop.id}`)}
 *       primaryColor="#0EA5E9"
 *     />
 *   ))}
 * </HorizontalScroll>
 */

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
  },
};

const badgeVariants = {
  hidden: { opacity: 0, y: -8 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1], delay: 0.1 },
  },
};

const priceVariants = {
  hidden: { opacity: 0, y: 10 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1], delay: 0.15 },
  },
};

function FeaturedPropertyCardBase({ property, onClick, primaryColor = '#0EA5E9', isMobile = false }) {
  const [imgError, setImgError] = useState(false);

  const formattedPrice = useMemo(() => {
    if (!property?.price && property?.price !== 0) return null;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 0,
    }).format(property.price);
  }, [property?.price]);

  const mainImage = property?.images?.[0] || null;
  const hasValidImage = mainImage && !imgError;

  const locationLabel = useMemo(() => {
    const { neighborhood, city } = property || {};
    if (neighborhood && city) return `${neighborhood}, ${city}`;
    return neighborhood || city || null;
  }, [property?.neighborhood, property?.city]);

  const handleImgError = useCallback(() => setImgError(true), []);

  const handleClick = useCallback(() => {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(8);
    }
    onClick?.(property);
  }, [onClick, property]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick]
  );

  return (
    <motion.article
      variants={cardVariants}
      initial="hidden"
      animate="show"
      role="button"
      tabIndex={0}
      aria-label={property?.title ? `Ver detalhes de ${property.title}` : 'Ver detalhes do imóvel'}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="group relative w-[280px] sm:w-[300px] md:w-[320px] shrink-0
        rounded-2xl border border-slate-200 dark:border-slate-700/60
        bg-white dark:bg-slate-900/80
        shadow-md hover:shadow-xl dark:shadow-black/20
        cursor-pointer overflow-hidden
        focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-950
        will-change-transform"
      style={{
        transform: 'translateZ(0)',
        '--tw-ring-color': primaryColor,
      }}
    >
      {/* Imagen + overlay */}
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100 dark:bg-slate-800">
        {hasValidImage ? (
          <motion.img
            src={mainImage}
            alt={property?.title || 'Imóvel'}
            loading="lazy"
            onError={handleImgError}
            className="h-full w-full object-cover will-change-transform"
            style={{ transform: 'translateZ(0)' }}
            initial={{ scale: 1 }}
            whileHover={{ scale: 1.06 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-800 dark:to-slate-700">
            <ImageIcon className="h-10 w-10 text-slate-400 dark:text-slate-500" />
          </div>
        )}

        {/* Gradiente para legibilidad del precio */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

        {/* Badge Destaque */}
        {property?.featured === true && (
          <motion.div
            variants={badgeVariants}
            initial="hidden"
            animate="show"
            className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-amber-400 px-2.5 py-1 text-xs font-semibold text-amber-950 shadow-sm"
          >
            <Star className="h-3 w-3 fill-amber-950" />
            Destaque
          </motion.div>
        )}

        {/* Badge de status */}
        {property?.status && (
          <motion.div
            variants={badgeVariants}
            initial="hidden"
            animate="show"
            className="absolute right-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur-sm dark:bg-slate-900/80 dark:text-slate-200"
          >
            {property.status}
          </motion.div>
        )}

        {/* Precio sobre la imagen */}
        {formattedPrice && (
          <motion.div
            variants={priceVariants}
            initial="hidden"
            animate="show"
            className="absolute inset-x-3 bottom-3"
          >
            <span className="text-xl font-bold text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.55)]">
              {formattedPrice}
            </span>
          </motion.div>
        )}
      </div>

      {/* Contenido */}
      <div className="px-4 py-3.5">
        <h3 className="truncate font-semibold text-slate-900 dark:text-white">
          {property?.title || 'Imóvel sem título'}
        </h3>

        {locationLabel && (
          <div className="mt-1 flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{locationLabel}</span>
          </div>
        )}

        {(property?.bedrooms != null || property?.bathrooms != null || property?.area != null) && (
          <div className="mt-2.5 flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
            {property?.bedrooms != null && (
              <span className="flex items-center gap-1">
                <Bed className="h-3.5 w-3.5" />
                {property.bedrooms}
              </span>
            )}
            {property?.bathrooms != null && (
              <span className="flex items-center gap-1">
                <Bath className="h-3.5 w-3.5" />
                {property.bathrooms}
              </span>
            )}
            {property?.area != null && (
              <span className="flex items-center gap-1">
                <Maximize2 className="h-3.5 w-3.5" />
                {property.area}m²
              </span>
            )}
          </div>
        )}
      </div>
    </motion.article>
  );
}

function arePropsEqual(prevProps, nextProps) {
  const prev = prevProps.property;
  const next = nextProps.property;

  return (
    prev?.id === next?.id &&
    prev?.price === next?.price &&
    prev?.images?.[0] === next?.images?.[0] &&
    prev?.featured === next?.featured &&
    prev?.status === next?.status &&
    prevProps.primaryColor === nextProps.primaryColor &&
    prevProps.isMobile === nextProps.isMobile &&
    prevProps.onClick === nextProps.onClick
  );
}

const FeaturedPropertyCard = memo(FeaturedPropertyCardBase, arePropsEqual);

FeaturedPropertyCard.displayName = 'FeaturedPropertyCard';

export { FeaturedPropertyCard };
export default FeaturedPropertyCard;